import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { Container, Row, Col, Nav } from 'react-bootstrap'
import { useLazyQuery } from '@apollo/client'
import dynamic from 'next/dynamic'

import styles from './style.module.scss'
import navStyles from '@shared/components/commonNav/style.module.scss'
import { pageHeaderLoader, tableLoader } from '@shared/libs/allLoader'
import Error from '@shared/components/error'
import { RANKINGS } from '@graphql/globalwidget/rankings.query'
import { dateCheck, convertDateAMPM } from '@shared-submodule/utils'
import useWindowSize from '@shared/hooks/windowSize'

import { useRouter } from 'next/router'
const Layout = dynamic(() => import('@shared/components/layout'))
const CustomLink = dynamic(() => import('@shared/components/customLink'))
const ThemeTable = dynamic(() => import('@shared/components/themeTable'), { loading: () => tableLoader() })
const PageHeader = dynamic(() => import('@shared/components/pageHeader'), { loading: () => pageHeaderLoader() })
const ATSAds = dynamic(() => import('@shared/components/atsAds'), { ssr: false })

const iccRankings = ({ data, seoData }) => {
  const router = useRouter()
  const [rankData, setRankData] = useState(data?.getRankings?.aResults)
  const initialCall = useRef(false)
  const [filter, setFilter] = useState({
    eMatchType: router?.query?.match || 'Tests',
    eRankType: router?.query?.rank || 'Teams',
    nLimit: 120,
    nSkip: 0
  })
  const [getRankData, { data: newRankData }] = useLazyQuery(RANKINGS)
  const personLabels = ['Rank', 'Name', 'Team', 'Rating']
  const teamLabels = ['Rank', 'Team', 'Rating', 'Points']
  const [width] = useWindowSize()

  const gender = router.pathname.split('/').pop()

  const handleMatchFilter = value => {
    if (value) {
      if (router.query.rank) {
        router.push({ query: { match: value || 'Tests', rank: router.query.rank } })
      } else {
        router.push({ query: { match: value || 'Tests' } })
      }
    } else {
      if (router.query.rank) {
        router.push({ query: { rank: router?.query?.rank || 'Teams' } })
      } else {
        router.push({ query: {} })
      }
    }
  }

  useEffect(() => {
    setFilter({ ...filter, eRankType: router?.query?.rank || 'Teams', eMatchType: router?.query?.match || 'Tests' })
  }, [router?.query])

  useEffect(() => {
    newRankData && setRankData(newRankData?.getRankings?.aResults)
  }, [newRankData])

  useEffect(() => {
    initialCall.current && getRankData({ variables: { input: filter } })
  }, [filter])

  useEffect(() => {
    initialCall.current = true
  }, [])
  return (
    <Layout
      data={{
        oSeo: {
          ...seoData?.getSeoData,
          sTitle: 'ICC Rankings',
          sDescription:
            'Latest ICC Rankings of ODI, T20I and Tests. Catch the latest Sports News today, Sports news headlines today, Sports news in English which includes Breaking News, National Sports News Today, Current Sports, Live Sport including Live Cricket Score from a Cricket Match'
        }
      }}>
      <section className="common-section pt-4">
        <ATSAds />
        <Container>
          <Row>
            <Col lg={9} className="left-content text-capitalize">
              <PageHeader name={`ICC Cricket Rankings - ${gender}`} />
              {rankData[0]?.dUpdated && (
                <div className={`${styles.lastupdate} mb-2`}>Last updated on : {convertDateAMPM(dateCheck(rankData[0].dUpdated))}</div>
              )}
              <Nav className={`${navStyles.commonNav} ${navStyles.pillsNav} text-uppercase`} variant="pills">
                <Nav.Item className={navStyles.item}>
                  <a className={`${filter.eMatchType === 'Odis' && navStyles.active} nav-link`} onClick={() => handleMatchFilter('Odis')}>
                    {'ODI'}
                  </a>
                </Nav.Item>
                <Nav.Item className={navStyles.item}>
                  <a className={`${filter.eMatchType === 'T20s' && navStyles.active} nav-link`} onClick={() => handleMatchFilter('T20s')}>
                    {'T20i'}
                  </a>
                </Nav.Item>
                <Nav.Item className={navStyles.item}>
                  <a className={`${filter.eMatchType === 'Tests' && navStyles.active} nav-link`} onClick={() => handleMatchFilter()}>
                    {'Test'}
                  </a>
                </Nav.Item>
              </Nav>
              <section className={styles.awardsTable}>
                <ThemeTable labels={filter.eRankType === 'Teams' ? teamLabels : personLabels} isNumbered={true}>
                  {rankData?.map(element => {
                    return (
                      <tr key={element._id} className={`${element.highlight && 'highlight'}`}>
                        <td>{element.nRank}</td>
                        <td>
                          {element?.oPlayer?.eTagStatus === 'a' && element?.oPlayer?.oSeo?.eStatus === 'a' ? (
                            <CustomLink href={`/${element?.oPlayer?.oSeo?.sSlug}`}>{element.sName}</CustomLink>
                          ) : (
                            element.sName
                          )}
                        </td>
                        {filter.eRankType !== 'Teams' && (
                          <td className="text-uppercase">
                            {width < 767 ? element?.oPlayer?.sCountry || element?.sTeam : element?.oPlayer?.sCountryFull || element?.sTeam}
                          </td>
                        )}
                        <td>{element.nRating}</td>
                        {filter.eRankType === 'Teams' && <td>{element.nPoints}</td>}
                      </tr>
                    )
                  })}
                </ThemeTable>
              </section>
            </Col>
            <Col lg={3} className="common-sidebar"></Col>
          </Row>
        </Container>
      </section>
    </Layout>
  )
}

iccRankings.propTypes = {
  data: PropTypes.object
}

export async function getServerSideProps({ res, resolvedUrl, query }) {
  try {
    res.setHeader('Cache-Control', 'public, max-age=420')

    const queryGraphql = (await import('@shared/components/queryGraphql')).default
    const GET_SEO_DATA = (await import('@graphql/article/article.query')).GET_SEO_DATA
    const { checkRedirectionStatus } = await import('@shared-submodule/utils')
    const { allRoutes } = await import('@shared-submodule/sm-constants/allRoutes')
    const { data: seoData } = await queryGraphql(GET_SEO_DATA, { input: { sSlug: allRoutes.iccRankings?.slice(1) } })
    // Check Redirection
    const { redirectStatus, eCode, returnObj, props } = checkRedirectionStatus(seoData?.getSeoData)
    if (redirectStatus && props) {
      res.statusCode = eCode
      return { props }
    } else if (redirectStatus) return returnObj

    const RANKINGS = (await import('@graphql/globalwidget/rankings.query')).RANKINGS
    const { data } = await queryGraphql(RANKINGS, {
      input: { eMatchType: query?.match || 'Tests', eRankType: query?.rank || 'Teams', eGender: 'M', nLimit: 10, nSkip: 0 }
    })
    return {
      props: {
        data,
        seoData
      }
    }
  } catch (e) {
    res.setHeader('Cache-Control', 'no-cache')
    const handleApiError = (await import('@shared-submodule/utils')).handleApiError
    const status = handleApiError(e, resolvedUrl)
    return status
  }
}

export default Error(iccRankings)
